import { Avatar, List, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { Box } from "@mui/system";
import Button from "components/common/Button";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import UserAvatarList from "components/common/UserAvatarList";
import AvatarItem from "components/common/UserAvatarList/item";
import { sanitize } from "dompurify";
import { uniq } from "lodash";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes, regExpForId } from "utils/CommonConstants";
import { getLoggedInUserHltId } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { getUserName } from "utils/StoreUtils";
import { fetchAssigneeDetailsAsync } from "../MyPlanDetails/MyPlanDetails.slice";
import "./Comments.scss";
import {
  createCommentAsync,
  fetchCommentAsync,
  fetchCommentUsersAsync,
  fetchPlanMembersAsync,
  reset
} from "./Comments.slice";
import { EVERYONE_MENTION, MentionInput } from "./MentionInput";

export const Comments = ({ sectionId, cardId, planId }: any) => {
  const dispatch = useAppDispatch();

  const regExpForStr = /\[{~(.*?)\~}]/g;
  const regExpForComment = /\[~~(.*?)\~~]/g;

  const loadingCount = useAppSelector((state) => state.Comments.loadingCount);
  const commentUsers = useAppSelector((state) => state.Comments.commentUsers);
  const commentLists = useAppSelector((state) => state.Comments.commentList);
  const { members, owners } = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const planMembersDetail = useAppSelector((state) => state.Comments.planMembers);
  const activeCard = useAppSelector((state: any) => state.app.activeActionCard);

  const [commentValue, setCommentValue] = useState<any>("");

  const isCardLevelComments = activeCard && cardId && cardId !== "null";

  const fetchPlanMembers = async () => {
    const response = await dispatch(fetchPlanMembersAsync({ planId: planId }));
    let membersList = uniq(response.payload.planMembers?.map((item: any) => item.hltId));
    if (membersList?.length) {
      const data = {
        value: [...membersList],
        size: [...membersList]?.length
      };
      dispatch(fetchAssigneeDetailsAsync(data));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const res = await dispatch(fetchCommentAsync(JSON.parse(JSON.stringify({ planId, sectionId, cardId }))));
      if (res.payload.meta.taggedUsers.length) {
        const data = {
          value: res.payload.meta.taggedUsers,
          size: res.payload.meta.taggedUsers.length
        };
        dispatch(fetchCommentUsersAsync(data));
      }
    };

    loadData();

    return () => {
      dispatch(reset());
    };
  }, []);

  const getTaggedUserIds = () => {
    const matches = commentValue.match(regExpForId) || [];
    const idArr = matches.map((match: string) => match.replace(/\[~~|\~~\]/g, ""));

    const hasEveryone = commentValue.includes(`[~~${EVERYONE_MENTION.id}~~]`);
    const loggedInUserHltId = getLoggedInUserHltId();

    let everyoneHltIds: any = [];
    if (hasEveryone) {
      if (isCardLevelComments) {
        everyoneHltIds = activeCard.assignee;
      } else {
        everyoneHltIds = uniq([...planMembersDetail.map((member: any) => member.hltId), ...members, ...owners]);
      }
    }

    return [...idArr, ...everyoneHltIds].filter((hltId) => hltId !== loggedInUserHltId);
  };

  const postComment = async () => {
    let finalStr = commentValue.replace(regExpForStr, "");

    const data = {
      sectionId,
      cardId,
      planId,
      commentList: [
        {
          comments: finalStr,
          taggedUserIds: getTaggedUserIds()
        }
      ]
    };
    const createCommentResponse = await dispatch(createCommentAsync(data));
    setCommentValue("");

    dispatch(
      fetchCommentUsersAsync({
        value: [...(createCommentResponse.payload[0].taggedUserIds || []), createCommentResponse.payload[0].createdBy],
        size: [...(createCommentResponse.payload[0].taggedUserIds || []), createCommentResponse.payload[0].createdBy]
          ?.length
      })
    );

    if (createCommentResponse.payload?.[0]?.taggedUserIds?.length) fetchPlanMembers();
  };

  const getReplacedComment = (id: any) => {
    const currId = id.replace("[~~", "").replace("~~]", "");
    const user = commentUsers?.find((u: any) => u.hltId === currId);
    return `<b>@${
      currId === EVERYONE_MENTION.id
        ? EVERYONE_MENTION.display
        : getUserNameWithConditionalCheck(user?.displayName, user?.firstName, user?.lastName)
    }</b>`;
  };

  return (
    <>
      <div className="comments">
        {loadingCount > 0 ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "180px",
              alignItems: "center",
              justifyContent: "center",
              display: "flex"
            }}
          >
            <Spinner />
          </Box>
        ) : (
          <>
            {commentLists && (
              <Typography color={tokens.Text.General.Secondary} showBold variant={"body2"}>
                {commentLists.length} Comments
              </Typography>
            )}
            <List className="list">
              {commentLists?.map((comment: any) => {
                if (commentUsers.length) {
                  const currUser = commentUsers.find((u: any) => u.hltId === comment.createdBy);
                  const userName = getUserNameWithConditionalCheck(
                    currUser?.displayName,
                    currUser?.firstName,
                    currUser?.lastName
                  );
                  const replacedComment = comment.comments.replace(regExpForComment, getReplacedComment);
                  return (
                    <Box
                      sx={{ paddingBottom: "16px", borderBottom: `1px solid ${tokens.Border.General.Tertiary}` }}
                      key={comment.id}
                    >
                      <ListItem sx={{ paddingLeft: 0, paddingRight: 0, margin: 0 }}>
                        <ListItemAvatar>
                          {currUser && (
                            <Avatar
                              className="avatar-list"
                              sx={{ width: "46px", height: "46px", backgroundColor: "unset" }}
                            >
                              <UserAvatarList users={[currUser]} />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography color={tokens.Text.General.PrimaryBody} showBold>
                              {userName}
                            </Typography>
                          }
                          secondary={comment.created}
                        />
                      </ListItem>
                      <Typography color={tokens.Text.General.PrimaryBody} variant={"body2"}>
                        <span
                          style={{ overflowWrap: "anywhere" }}
                          dangerouslySetInnerHTML={{ __html: sanitize(replacedComment) }}
                        ></span>
                      </Typography>
                    </Box>
                  );
                }

                return null;
              })}
            </List>
          </>
        )}
        <div className="input">
          <Box sx={{ display: "flex" }}>
            <AvatarItem size="regular" variant="colored" user={getUserName(true, true)} />
            <MentionInput
              commentValue={commentValue}
              setCommentValue={setCommentValue}
              isCardLevelComments={isCardLevelComments}
            />
          </Box>
          <div className="action-buttons">
            <Button
              variant="primary"
              label="Submit"
              width="160px"
              accessType={accessTypes.update}
              onClick={postComment}
            />
          </div>
        </div>
      </div>
    </>
  );
};
